import { format } from "date-fns";
import { AlertTriangle, ChevronLeft, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DownloadSyllabus from "./DownloadSyllabus";
import { Fade } from "@courseweb/ui";
import { toPrettySemester } from "@/helpers/semester";
import CourseTagList from "@/components/Courses/CourseTagsList";
import {
  colorMapFromCourses,
  createTimetableFromCourses,
} from "@/helpers/timetable";
import { MinimalCourse, RawCourseID } from "@/types/courses";
import {
  hasTimes,
  getScoreType,
  getFormattedClassCode,
} from "@/helpers/courses";
import { Button } from "@courseweb/ui";
import { Separator } from "@courseweb/ui";
import { Alert, AlertDescription } from "@courseweb/ui";
import { Badge } from "@courseweb/ui";
import { CourseDefinition } from "@/config/supabase";
import { ScrollArea, ScrollBar } from "@courseweb/ui";
import { timetableColors } from "@courseweb/shared";
import { lazy, Suspense } from "react";
import { Language } from "@/types/settings";
import { sanitizeCourseHtml } from "@/lib/sanitizeHtml";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@courseweb/ui";
import { Dialog, DialogContent, DialogTrigger } from "@courseweb/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@courseweb/ui";
import { cn } from "@courseweb/ui";
import ShareCourseButton from "./ShareCourseButton";
import DateContributeForm from "./DateContributeForm";
import client from "@/config/api";
import useDictionary from "@/dictionaries/useDictionary";
import { useQuery } from "@tanstack/react-query";
import CourseDetailsSkeleton from "./CourseDetailsSkeleton";
import { useCourseLink } from "@/components/Courses/CourseDialog";

const PDFViewerDynamic = lazy(
  () => import("@/components/CourseDetails/PDFViewer"),
);
const SelectCourseButtonDynamic = lazy(
  () => import("@/components/Courses/SelectCourseButton"),
);

const TOCNavItem = ({
  href,
  children,
  label,
  active,
}: {
  href: string;
  children?: React.ReactNode;
  label: string;
  active?: boolean;
}) => {
  return (
    <li className="mt-0 pt-2">
      <a
        href={href}
        className={`inline-block no-underline transition-colors hover:text-foreground ${active ? "font-medium text-foreground" : "text-muted-foreground"}`}
      >
        {label}
      </a>
      {children}
    </li>
  );
};

const CrossDisciplineTagList = ({ course }: { course: CourseDefinition }) => {
  return (
    <div className="flex flex-row gap-2 flex-wrap">
      {course.cross_discipline?.map((m, index) => (
        <div
          key={index}
          className="flex flex-row items-center justify-center min-w-[65px] space-x-2 px-2 py-2 select-none rounded-md text-sm bg-neutral-200 dark:bg-neutral-800"
        >
          {m}
        </div>
      ))}
    </div>
  );
};

const CourseDetailContainer = ({
  lang,
  courseId,
  bottomAware = false,
  modal = false,
}: {
  lang: Language;
  courseId: string;
  bottomAware?: boolean;
  modal?: boolean;
}) => {
  const dict = useDictionary();
  const { openCourse } = useCourseLink();

  // Use React Query to fetch the course data
  const {
    data: course,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["course", courseId, "syllabus"],
    queryFn: async () => {
      const res = await client.course[":courseId"].syllabus.$get({
        param: { courseId },
      });
      return res.json();
    },
  });

  // Use React Query for reviews
  const { data: reviews = [] } = useQuery({
    queryKey: ["course", courseId, "ptt"],
    queryFn: async () => {
      const res = await client.course[":courseId"].ptt.$get({
        param: { courseId },
      });
      return res.json();
    },
    enabled: !!course, // Only fetch if course data is available
  });

  // Use React Query for related courses
  const { data: otherClasses = [] } = useQuery({
    queryKey: ["course", courseId, "related"],
    queryFn: async () => {
      const res = await client.course[":courseId"].related.$get({
        param: { courseId },
      });
      return res.json();
    },
    enabled: !!course, // Only fetch if course data is available
  });

  // Dynamic SEO metadata for course detail pages (full-page view only)
  const courseJsonLd =
    !modal && course
      ? {
          "@context": "https://schema.org",
          "@type": "Course",
          name: course.name_zh,
          alternateName: course.name_en,
          description:
            course.course_syllabus?.brief ??
            `清大 ${course.department} ${course.name_zh} 課程`,
          provider: {
            "@type": "EducationalOrganization",
            name: "National Tsing Hua University",
            alternateName: "國立清華大學",
            url: "https://www.nthu.edu.tw",
          },
          instructor: (course.teacher_zh ?? []).map((t) => ({
            "@type": "Person",
            name: t,
          })),
          courseCode: `${course.department} ${course.course}-${course.class}`,
          educationalLevel: "University",
          inLanguage: "zh-TW",
          url: `https://nthumods.com/${lang}/courses/${course.raw_id}`,
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "TWD",
            availability: "https://schema.org/InStock",
          },
        }
      : null;

  const breadcrumbData =
    !modal && course
      ? [
          { name: "首頁", url: `https://nthumods.com/${lang}` },
          { name: "課程", url: `https://nthumods.com/${lang}/courses` },
          {
            name: course.department,
            url: `https://nthumods.com/${lang}/courses?department=${encodeURIComponent(course.department)}`,
          },
          {
            name: course.name_zh,
            url: `https://nthumods.com/${lang}/courses/${course.raw_id}`,
          },
        ]
      : null;

  const breadcrumbJsonLd = breadcrumbData
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbData.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      }
    : null;

  const webPageJsonLd =
    !modal && course
      ? {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: `${course.name_zh} | NTHUMods`,
          description: `清大 ${toPrettySemester(course.semester)} ${course.name_zh} 課程資訊`,
          url: `https://nthumods.com/${lang}/courses/${course.raw_id}`,
          inLanguage: lang === "en" ? "en-US" : "zh-TW",
          isPartOf: { "@type": "WebSite", url: "https://nthumods.com" },
        }
      : null;

  // Handle loading state
  if (isLoading) {
    return <CourseDetailsSkeleton />;
  }

  // Handle error state
  if (error || !course) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <div className="py-6 px-4">
          <div className="flex flex-col gap-2 border-l border-neutral-500 pl-4 pr-6">
            <h1 className="text-2xl font-bold">404</h1>
            <p className="text-xl">找不到課程</p>

            <Link to="../">
              <Button size="sm" variant="outline">
                <ChevronLeft /> Back
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  const missingSyllabus = course.course_syllabus == null;

  // times might not be available, check if it is empty list or its items are all empty strings
  const showTimetable = hasTimes(course as MinimalCourse);

  const colorMap = colorMapFromCourses(
    [course as MinimalCourse].map((c) => c.raw_id),
    timetableColors[Object.keys(timetableColors)[0]],
  );
  const timetableData = showTimetable
    ? createTimetableFromCourses([course as MinimalCourse], colorMap)
    : [];

  return (
    <Fade>
      {!modal && course && (
        <Helmet>
          <title>{`${course.name_zh} ${course.teacher_zh?.join("、")} - ${toPrettySemester(course.semester)} 清大${course.department}課程 | NTHUMods`}</title>
          <meta
            name="description"
            content={`清大 ${toPrettySemester(course.semester)} ${course.name_zh}（${course.name_en}）課程資訊，${course.teacher_zh?.join("、")} 授課。查看課程大綱、歷年評分記錄與學生心得。`}
          />
          <link
            rel="canonical"
            href={`https://nthumods.com/${lang}/courses/${course.raw_id}`}
          />
          <link
            rel="alternate"
            hrefLang="zh-TW"
            href={`https://nthumods.com/zh/courses/${course.raw_id}`}
          />
          <link
            rel="alternate"
            hrefLang="en"
            href={`https://nthumods.com/en/courses/${course.raw_id}`}
          />
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`https://nthumods.com/zh/courses/${course.raw_id}`}
          />
          <meta
            property="og:title"
            content={`${course.name_zh} - ${course.department} | NTHUMods`}
          />
          <meta
            property="og:description"
            content={`清大 ${course.name_zh}（${course.name_en}），${course.teacher_zh?.join("、")} 授課，${toPrettySemester(course.semester)} 學期。查看課程大綱與歷年資訊。`}
          />
          <meta
            property="og:url"
            content={`https://nthumods.com/${lang}/courses/${course.raw_id}`}
          />
          <meta name="twitter:card" content="summary" />
          <meta
            name="twitter:title"
            content={`${course.name_zh} - ${course.department} | NTHUMods`}
          />
          <meta
            name="twitter:description"
            content={`清大 ${course.name_zh}，${course.teacher_zh?.join("、")} 授課，${toPrettySemester(course.semester)} 學期。`}
          />
          {courseJsonLd && (
            <script type="application/ld+json">
              {JSON.stringify(
                [courseJsonLd, breadcrumbJsonLd, webPageJsonLd].filter(Boolean),
              )}
            </script>
          )}
        </Helmet>
      )}
      <div
        className={cn(
          "flex flex-col pb-6 relative",
          modal
            ? "max-w-[min(72rem,calc(100vw-52px))]"
            : "max-w-[min(72rem,calc(100vw-16px))]",
        )}
      >
        <div className={cn("flex flex-col gap-4 pb-20 md:pb-0")}>
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="space-y-4 flex-1 w-full">
              <div className="space-y-2">
                <div className="font-semibold text-base ">
                  {toPrettySemester(course.semester)} 學期
                </div>
                <div className="font-bold text-xl mb-4 text-nthu-600">{`${course?.department} ${course?.course}-${course?.class}`}</div>
                <h1 className="font-semibold text-3xl flex flex-row flex-wrap gap-1">
                  <span>{course!.name_zh}</span>
                  <span>{course?.teacher_zh?.join(",") ?? ""}</span>
                </h1>
                <h2 className="font-semibold text-xl flex flex-row flex-wrap gap-1">
                  <span>{course!.name_en}</span>
                  <span>{course?.teacher_en?.join(",") ?? ""}</span>
                </h2>
              </div>
              <CourseTagList course={course} />
              {course.venues ? (
                course.venues.map((vn, i) => (
                  <p
                    key={vn}
                    className="text-blue-600 dark:text-blue-400 text-sm"
                  >
                    {vn}{" "}
                    <span className="text-black dark:text-white">
                      {course.times![i]}
                    </span>
                  </p>
                ))
              ) : (
                <p>No Venues</p>
              )}
              <CrossDisciplineTagList course={course} />
            </div>
            <div className="hidden md:flex flex-col gap-2 absolute top-0 right-0 mt-4 mr-4">
              <Suspense fallback={null}>
                <SelectCourseButtonDynamic courseId={course.raw_id} />
              </Suspense>
              <ShareCourseButton
                link={`https://nthumods.com/courses/${course.raw_id}`}
                displayName={`${course.name_zh} ${course.teacher_zh.join(",")}`}
              />
            </div>
            <div
              className={cn(
                "md:hidden fixed left-0 pt-2 px-4 shadow-md w-full h-16 flex flex-row gap-2 bg-background z-50",
                bottomAware ? "bottom-20" : "bottom-0",
              )}
            >
              <ShareCourseButton
                link={`https://nthumods.com/courses/${course.raw_id}`}
                displayName={`${course.name_zh} ${course.teacher_zh.join(",")}`}
              />
              <div className="flex-1 flex flex-col">
                <Suspense fallback={null}>
                  <SelectCourseButtonDynamic courseId={course.raw_id} />
                </Suspense>
              </div>
            </div>
          </div>
          <Separator />
          <div className={"flex flex-col-reverse lg:flex-row gap-6 w-full"}>
            <div className="flex flex-col gap-4 min-w-0 lg:max-w-[calc(100%-284px)]">
              {!missingSyllabus && (
                <div className="flex flex-col gap-2">
                  <h3 className="font-semibold text-xl" id="brief">
                    {dict.course.details.brief}
                  </h3>
                  <p className="whitespace-pre-line text-sm">
                    {course.course_syllabus!.brief}
                  </p>
                </div>
              )}
              {!missingSyllabus && (
                <div className="flex flex-col gap-2">
                  <h3 className="font-semibold text-xl" id="description">
                    {dict.course.details.description}
                  </h3>
                  <p className="whitespace-pre-line text-sm">
                    {course.course_syllabus!.content ?? (
                      <div className="flex flex-col gap-2">
                        <DownloadSyllabus courseId={course.raw_id} />
                        <Suspense fallback={null}>
                          <PDFViewerDynamic
                            file={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/syllabus/${encodeURIComponent(course.raw_id)}.pdf`}
                          />
                        </Suspense>
                      </div>
                    )}
                  </p>
                </div>
              )}
              {course?.prerequisites && (
                <div className="flex flex-col gap-2">
                  <h3 className="font-semibold text-xl" id="prerequesites">
                    {dict.course.details.prerequesites}
                  </h3>
                  <div
                    className="whitespace-pre-line text-sm"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeCourseHtml(course.prerequisites),
                    }}
                  />
                </div>
              )}
              {/* {showTimetable && <div className="flex flex-col gap-2">
                            <h3 className="font-semibold text-xl" id="timetable">{dict.course.details.timetable}</h3>
                            <TimetableDynamic timetableData={timetableData} />
                        </div>} */}
              {reviews.length > 0 && (
                <div className="flex flex-col gap-2">
                  <h3 className="font-semibold text-xl" id="ptt">
                    {dict.course.details.ptt_title}
                  </h3>
                  <Alert>
                    <AlertTriangle />
                    <AlertDescription>
                      {dict.course.details.ptt_disclaimer}
                    </AlertDescription>
                  </Alert>
                  <ScrollArea className="w-full whitespace-nowrap">
                    <div className="flex space-x-4 pr-4">
                      {reviews.map((m, index) => (
                        <Dialog key={index}>
                          <DialogTrigger asChild>
                            <Card className="max-w-lg shrink-0">
                              <CardHeader>
                                <CardTitle className="text-lg">
                                  {index + 1}.{" "}
                                  {format(new Date(m.date ?? 0), "yyyy-MM-dd")}{" "}
                                  的心得
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <article className="whitespace-pre-line line-clamp-4 text-sm">
                                  {m.content}
                                </article>
                              </CardContent>
                            </Card>
                          </DialogTrigger>
                          <DialogContent className="">
                            <ScrollArea className="max-h-[90vh] whitespace-nowrap">
                              <p className="whitespace-pre-line text-sm">
                                {m.content}
                              </p>
                            </ScrollArea>
                          </DialogContent>
                        </Dialog>
                      ))}
                    </div>
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                </div>
              )}
              {course.course_scores && (
                <div className="flex flex-col gap-2">
                  <h3 className="font-semibold text-xl" id="scores">
                    {dict.course.details.scores}
                  </h3>
                  {/* TODO: make scores prettier with a graph */}
                  <p className="text-sm">
                    <span className="font-bold">
                      {dict.course.details.average}
                      {
                        dict.course.details.score_types[
                          course.course_scores.type as "gpa" | "percent"
                        ]
                      }
                      :
                    </span>{" "}
                    {course.course_scores.average}
                  </p>
                  <p className="text-sm">
                    <span className="font-bold">
                      {dict.course.details.standard_deviation}:
                    </span>{" "}
                    {course.course_scores.std_dev}
                  </p>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <div className="flex flex-row">
                  <h3 className="font-semibold text-xl flex-1" id="other">
                    {dict.course.details.related_courses}
                  </h3>
                  <Button variant="ghost" asChild>
                    <Link
                      to={`/${lang}/courses?nthu_courses%5BrefinementList%5D%5Bdepartment%5D%5B0%5D=${course.department}&nthu_courses%5Bquery%5D=${course.name_zh} ${course.teacher_zh.join(" ")}`}
                    >
                      查看更多 <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </div>
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[110px] px-2">
                        學期/時間地點
                      </TableHead>
                      <TableHead className="w-[80px] px-2">開課教授</TableHead>
                      <TableHead className="w-28 px-2">歷年平均成績</TableHead>
                      <TableHead className="w-14 p-0"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {otherClasses.map((m, index) => (
                      <TableRow key={index}>
                        <TableCell className="px-2">
                          <div className="flex flex-col gap-1">
                            <p>{toPrettySemester(m.semester)}</p>
                            <div className="flex flex-col">
                              {m.times.map((t, i) => (
                                <p key={i} className="text-xs text-gray-500">
                                  {m.venues[i]} {t}
                                </p>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-2">
                          {m.teacher_zh?.join(",")}
                        </TableCell>
                        <TableCell>
                          {m.course_scores && (
                            <div className="flex flex-col gap-1 text-xs">
                              <p>
                                {getScoreType(m.course_scores.type)}{" "}
                                {m.course_scores.average}
                              </p>
                              <p>標準差 {m.course_scores.std_dev}</p>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="p-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openCourse(m.raw_id)}
                          >
                            <ArrowRight size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="w-full lg:w-[284px] flex flex-col gap-4">
              <ScrollArea className="hidden lg:flex">
                <div className="space-y-2">
                  <ul className="m-0 list-none">
                    {!missingSyllabus && (
                      <TOCNavItem
                        href="#brief"
                        label={dict.course.details.brief}
                      />
                    )}
                    {!missingSyllabus && (
                      <TOCNavItem
                        href="#description"
                        label={dict.course.details.description}
                      />
                    )}
                    {course?.prerequisites && (
                      <TOCNavItem
                        href="#prerequesites"
                        label={dict.course.details.prerequesites}
                      />
                    )}
                    {/* {showTimetable && <TOCNavItem href="#timetable" label={dict.course.details.timetable} />} */}
                    {course.course_scores && (
                      <TOCNavItem
                        href="#scores"
                        label={dict.course.details.scores}
                      />
                    )}
                    {reviews.length > 0 && (
                      <TOCNavItem href="#ptt" label={dict.course.details.ptt} />
                    )}
                    <TOCNavItem
                      href="#other"
                      label={dict.course.details.related_courses}
                    />
                  </ul>
                </div>
              </ScrollArea>
              {(course.note ?? "").trim().length > 0 && (
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold text-base">
                    {dict.course.details.remarks}
                  </h3>
                  <p className="text-sm">{course.note}</p>
                </div>
              )}
              {(course.restrictions ?? "").trim().length > 0 && (
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold text-base">
                    {dict.course.details.restrictions}
                  </h3>
                  <p className="text-sm">{course.restrictions}</p>
                </div>
              )}
              {(course.compulsory_for ?? []).length > 0 && (
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold text-base">
                    {dict.course.details.compulsory}
                  </h3>
                  <div className="flex flex-row gap-2 flex-wrap">
                    {course.compulsory_for?.map((m, index) => (
                      <Link
                        key={index}
                        to={`/${lang}/courses?nthu_courses%5BrefinementList%5D%5Bcompulsory_for%5D%5B0%5D=${course.compulsory_for}`}
                      >
                        <Badge variant="outline">
                          {getFormattedClassCode(m, course.semester, lang)}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {(course.elective_for ?? []).length > 0 && (
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold text-base">
                    {dict.course.details.elective}
                  </h3>
                  <div className="flex flex-row gap-2 flex-wrap">
                    {course.elective_for?.map((m, index) => (
                      <Link
                        key={index}
                        to={`/${lang}/courses?nthu_courses%5BrefinementList%5D%5Belective_for%5D%5B0%5D=${course.elective_for}`}
                      >
                        <Badge variant="outline">
                          {getFormattedClassCode(m, course.semester, lang)}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {(course.first_specialization ?? []).length > 0 && (
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold text-base">
                    {dict.course.details.first_specialization}
                  </h3>
                  <div className="flex flex-row gap-2 flex-wrap">
                    {course.first_specialization?.map((m, index) => (
                      <Link
                        key={index}
                        to={`/${lang}/courses?nthu_courses%5BrefinementList%5D%5Bfirst_specialization%5D%5B0%5D=${course.first_specialization}`}
                      >
                        <Badge variant="outline">{m}</Badge>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {(course.second_specialization ?? []).length > 0 && (
                <div className="flex flex-col gap-1">
                  <h3 className="font-semibold text-base">
                    {dict.course.details.second_specialization}
                  </h3>
                  <div className="flex flex-row gap-2 flex-wrap">
                    {course.second_specialization?.map((m, index) => (
                      <Link
                        key={index}
                        to={`/${lang}/courses?nthu_courses%5BrefinementList%5D%5Bsecond_specialization%5D%5B0%5D=${course.second_specialization}`}
                      >
                        <Badge variant="outline">{m}</Badge>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-2 flex-wrap">
                  <p className="text-xs text-gray-500">
                    Details Updated:{" "}
                    {format(new Date(course.updated_at), "yyyy-MM-dd HH:mm")}
                  </p>
                  <p className="text-xs text-gray-500">
                    Syllabus Updated:{" "}
                    {format(
                      new Date(course.course_syllabus?.updated_at ?? 0),
                      "yyyy-MM-dd HH:mm",
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fade>
  );
};

export default CourseDetailContainer;
